<script setup lang="ts">
import InfoModalBtnArea from '@/components/AccountParts/InfoModalBtnArea.vue';
import { ref, computed, watch } from 'vue'
import InfoModal from "@/components/Common/InfoModal.vue"
import i18n from '@/lang/i18n';

const emits = defineEmits(['update:modelValue', 'exec'])
const props = defineProps<{
  modelValue: boolean
  accountId: string | null
  email: string
  password: string
  name: string[]
  isLoading: boolean
  isDriverMode: boolean
  roleAssignAuthority: boolean
  update: (bool: boolean) => void
}>()
const passwordMask = ref(true as boolean)
const isCopied = ref(false as boolean)
const passwordMessage = i18n.global.t('pages.accounts.messages.passwordMessage')

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits("update:modelValue", value);
  }
})

const displayPassword = computed(() => {
  if (!props.password) return passwordMessage
  if (passwordMask.value) {
    let password = "*"
    for (let i = 1; i < props.password.length; i++) {
      password = `${password}*`
    }
    return password
  }
  return props.password
})

function isMaskPassword(bool: boolean) {
  passwordMask.value = bool
}

const copyedValue = computed(() => {
  const password = props.password ?? passwordMessage
  return `${i18n.global.t('pages.accounts.accountName')}: ${props.name[0]}${props.name[1]}\n
    ${i18n.global.t('pages.accounts.form.emailAddress')}: ${props.email}\n
    ${i18n.global.t('pages.accounts.form.password')}: ${password}`
})

function changeModalStatus(bool: boolean) {
  modelValue.value = bool
}

watch(() => modelValue.value, (status) => {
  if (!status) {
    isCopied.value = false
  }
})
</script>
<template>
  <InfoModal
    v-model="modelValue"
    modalWidth="30%"
  >
    <template v-slot:text>
      <div>
        <div style="border-bottom: 2px solid rgb(21, 130, 156);" class="mb-6">
          <h5
            class="fw-bold mb-2"
          >
            {{ i18n.global.t('pages.accounts.DriverModelInfoModal.infoMessagesRow1') }}
          </h5>
          <h5
            v-if="!props.accountId"
            class="fw-bold mb-2"
          >
            {{ i18n.global.t('pages.accounts.DriverModelInfoModal.infoMessagesRow2') }}
          </h5>
        </div>
        <v-row>
          <v-col class="pb-1 text-body-medium" cols="4">
            <div class="d-flex h-100 align-center">
              <span class="material-symbols-outlined fs-10 mr-1">person</span>
              <span>{{ i18n.global.t('pages.accounts.accountName') }}:</span>
            </div>
          </v-col>
          <v-col class="pb-1 text-body-medium" cols="8" style="position: relative">
            <span>{{ props.name[0] }}{{ props.name[1] }}</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pb-1 text-body-medium" cols="4">
            <div class="d-flex h-100 align-center">
              <span class="material-symbols-outlined fs-10 mr-1">mail</span>
              <span>{{ i18n.global.t('pages.accounts.form.emailAddress') }}:</span>
            </div>
          </v-col>
          <v-col class="pb-1 text-body-medium" cols="8" style="position: relative">
            <span>{{ props.email }}</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="text-body-medium" cols="4">
            <div class="d-flex h-100 align-center">
              <span class="material-symbols-outlined fs-10 mr-1">key_vertical</span>
              <span>{{ i18n.global.t('pages.accounts.form.password') }}:</span>
            </div>
          </v-col>
          <v-col class="text-body-medium" cols="8" style="position: relative">
            <div  class="d-flex">
              <div>{{ displayPassword }}</div>
              <div v-if="props.password">
                <div id="eye-icon-wrapper" class="d-flex">
                  <svg @click="isMaskPassword(true)" v-show="!passwordMask" viewBox="0 0 576 512">
                    <path
                      d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM432 256c0 79.5-64.5 144-144 144s-144-64.5-144-144s64.5-144 144-144s144 64.5 144 144zM288 192c0 35.3-28.7 64-64 64c-11.5 0-22.3-3-31.6-8.4c-.2 2.8-.4 5.5-.4 8.4c0 53 43 96 96 96s96-43 96-96s-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6z"
                    />
                  </svg>
                  <svg @click="isMaskPassword(false)" v-show="passwordMask" viewBox="0 0 640 512">
                    <path
                      d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c5.2-11.8 8-24.8 8-38.5c0-53-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zm223.1 298L373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </template>
    <template v-slot:footer>
      <InfoModalBtnArea
        v-model="isCopied"
        :copyText="copyedValue"
        :accountId="props.accountId"
        :isLoading="props.isLoading"
        :isDriverMode="props.isDriverMode"
        :roleAssignAuthority="props.roleAssignAuthority"
        :update="props.update"
        :changeModalStatus="changeModalStatus"
      />
    </template>
  </InfoModal>
</template>
<style scoped>
#eye-icon-wrapper {
  right: -5px;
  width: 80px;
}

#eye-icon-wrapper:hover {
  cursor: pointer;
  opacity: 0.5;
}
</style>